import { Header } from "@general-backoffice/core";
import { Loading } from "@general-backoffice/core/index";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";

import useReferrersHierarchyStats from "../../../../../components/adminStats/hooks/useReferrersHierarchyStats";
import ReferrersStatsTableCard from "../../../../../components/adminStats/ReferrersStatsTableCard";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReferrerHierarchyStat = any;

const AffiliatesHierarchy: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.adminStatsPages.affiliates" });
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const {
    rawReferrersHierarchyStats,
    referrersHierarchyStats = [],
    isLoading: isLoadingReferrersHierarchyStats,
    ...referrersHierarchyProps
  } = useReferrersHierarchyStats({
    filters: { referredBy: id || null },
    depsToReset: [id],
    pagination: { pageSize: 5, page: 1 },
  });

  const [referrer, setReferrer] = useState<ReferrerHierarchyStat | null>(null);
  useEffect(() => {
    let newReferrer: ReferrerHierarchyStat | null = null;
    if (rawReferrersHierarchyStats && id) {
      newReferrer = rawReferrersHierarchyStats.find((r) => `${r?.referrer?.id}` === id) || null;
    }
    if (id && rawReferrersHierarchyStats && !newReferrer) {
      navigate("/stats/affiliates/affiliates-hierarchy");
    } else {
      setReferrer(newReferrer);
    }
  }, [rawReferrersHierarchyStats, id, navigate]);

  const referrerParent = useMemo(
    () =>
      referrer?.referredBy
        ? rawReferrersHierarchyStats?.find((r) => `${r?.referrer?.id}` === referrer?.referredBy)
        : null,
    [referrer, rawReferrersHierarchyStats]
  );

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}></Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col xl={12}>
            {referrer ? (
              <>
                <Button
                  color="white"
                  className="mx-0 p-4 w-100 shadow-none rounded-0 rounded-top"
                  style={{ zIndex: 1, marginBottom: -57 }}
                  onClick={() => navigate(`/stats/affiliates/affiliates-hierarchy/${referrerParent?.referrer?.id}`)}
                >
                  <span>
                    <i className="fas fa-chevron-left" aria-hidden="true" /> {t("goBack")}
                  </span>
                </Button>
                <ReferrersStatsTableCard
                  title=""
                  referrers={[referrer]}
                  isLoading={isLoadingReferrersHierarchyStats}
                  filtersToShow={[]}
                  columnsToHide={[
                    "number",
                    "activeBots",
                    "charged",
                    "activeBalance",
                    "inactiveBalance",
                    "totalProfit",
                    "totalLosses",
                  ]}
                />
              </>
            ) : (
              <Card>
                {isLoadingReferrersHierarchyStats ? (
                  <div className="pt-1 pb-6">
                    <Loading.Line />
                  </div>
                ) : (
                  <CardBody className="font-weight-bold text-default">{t("noParent")}</CardBody>
                )}
              </Card>
            )}
          </Col>

          <Col xl={12}>
            <ReferrersStatsTableCard
              {...referrersHierarchyProps}
              title={t("broughtAffiliates")}
              referrers={referrersHierarchyStats}
              totalReferrers={referrersHierarchyProps?.pagination?.total}
              isLoading={isLoadingReferrersHierarchyStats}
              filtersToShow={[]}
              columnsToHide={[
                "number",
                "activeBots",
                "charged",
                "activeBalance",
                "inactiveBalance",
                "totalProfit",
                "totalLosses",
              ]}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default AffiliatesHierarchy;
